// Copyright (c) Docugami, Inc. All rights reserved.

import { chunkSelectedColor } from "components/shell/presentation/AppStyles";
import { useEffect, useMemo } from "react";
import { frontendSlice, selectPropertyById } from "store/frontend/frontendSlice";
import { selectClusterId } from "store/reports/selectors";
import { selectSelectedChunkInstance } from "store/sdvSelectors";
import { setPreviewChunks, removePreviewChunkSet, setCurrentSelectedPage } from "store/sdvSlice";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { useAppDispatch, useAppSelector } from "./redux";
import { isSpanIdAddedChunk } from "store/dgml/dgmlTypes";
import { selectMappedTokens } from "store/dgml/dgmlSelectors";
import { globalEngine } from "components/doc_hosting/doc_hosts/web/presentation/WebDocHost";
import { createFocusViewEntity } from "sdv/engine/EntityFactory";

export const usePropertySelectionBehavior = () => {
    const docsetId = useAppSelector(selectClusterId);
    const fileId = useAppSelector(state => state.reportsSlice.fileId ?? '');
    const selectedSpanId = useAppSelector(selectSelectedChunkInstance)?.spanId;
    const isProperty = isSpanIdAddedChunk(selectedSpanId);
    const mappedTokens = useAppSelector(state => selectMappedTokens(state, docsetId, fileId));
    const dispatch = useAppDispatch();

    const property = useAppSelector(state => {
        if (!!isProperty) {
            const propertyState = frontendSlice.endpoints.listDocumentProperties.select({ docsetId, fileId })(state);
            if (!!propertyState?.data) {
                return selectPropertyById(propertyState.data, SpanIdUtils.parse(selectedSpanId!).subtreeId!)
            }
        }
    });

    const propertySpanId = useMemo(() => !!property?.id ? SpanIdUtils.createAddedChunk(fileId, property.id).value : '', [fileId, property?.id]);
    const isSelected = propertySpanId === selectedSpanId && !!selectedSpanId;
    const tokenRanges = property?.tokenRanges;
    const pageNumber = property?.pageNumber;

    useEffect(() => {
        if (isSelected) {
            if (!!tokenRanges?.length) {
                dispatch(setPreviewChunks({
                    id: propertySpanId,
                    chunks: {
                        spanIds: tokenRanges?.map(tr => SpanIdUtils.create(fileId, mappedTokens.tokenRangeToTokenKeyRange(tr).fromTokenKey, mappedTokens.tokenRangeToTokenKeyRange(tr).toTokenKey).value) ?? [],
                        color: chunkSelectedColor
                    }
                }));

                return () => {
                    dispatch(removePreviewChunkSet(propertySpanId))
                }
            }
            else if (pageNumber !== undefined) {
                const index = Math.max(0, pageNumber - 1);
                dispatch(setCurrentSelectedPage(index));
            }
        }
    }, [dispatch, fileId, mappedTokens, propertySpanId, tokenRanges, isSelected, pageNumber]);

    useEffect(() => {
        if (!!tokenRanges?.length) {
            globalEngine?.queuePreUpdateCommand((entityManager, resources) => {
                if (resources.set("selectedEntityName", selectedSpanId!)) {
                    createFocusViewEntity(entityManager, tokenRanges[0], true);
                }
            });
        }
    }, [selectedSpanId, tokenRanges])
}
